/**
 * TechniciensPage - Page de gestion des techniciens
 * Liste des sous-traitants avec détails
 * Permet de créer un compte mobile pour un technicien
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, useHasPermission } from '../SupabaseWrapper';
import { useViewport } from '../hooks/useViewport';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { phoneInputStyles } from '../styles/phoneInput';
import { messageCreation, messageModificationEmail, messageRenvoiSms } from '../utils/messagesCompteTechnicien';
import type { MessageCompte } from '../utils/messagesCompteTechnicien';
import { versE164, paysDuNumero } from '../utils/telephoneDom';
import {
  WrenchIcon,
  RefreshIcon,
  SearchIcon,
  CloseIcon,
  MapPinIcon,
  PhoneIcon,
  MailIcon,
  UserIcon,
  UserPlusIcon,
  CheckIcon,
  ChevronRightIcon,
  ShieldIcon,
  KeyIcon
} from '../icons';

// Types
interface Technicien {
  code: string;
  nom: string;
  codePostal: string;
  ville: string;
  telephone: string;
  mobile: string;
  email: string;
}

interface TechnicienDetail {
  identification: {
    code: string;
    nom: string;
    typeFournisseur: string;
  };
  adresse: {
    adresse1: string;
    adresse2: string;
    adresse3: string;
    codePostal: string;
    ville: string;
    telephone1: string;
    telephone2: string;
    fax: string;
    responsableContact: string;
  };
  contacts: Array<{
    nomCorrespondant: string;
    ligneDirecte: string;
    mobile: string;
    fax: string;
    email: string;
    observations: string;
  }>;
}

interface CreateAccountForm {
  company_name: string;
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
}

// URL API backend - vide car même origine (frontend servi par Node.js)
const API_URL = '';

// Props du composant
interface TechniciensPageProps {
  refreshTrigger?: number;
}

function TechniciensPage({ refreshTrigger = 0 }: TechniciensPageProps) {
  const { user: _currentUser } = useAuth();
  // RBAC : permissions
  const canView = useHasPermission('technicians.view');
  const canCreateAccount = useHasPermission('technicians.create_account');
  const canRevokeAccount = useHasPermission('technicians.revoke_account');
  const canUpdateEmail = useHasPermission('technicians.update_email');

  const [techniciens, setTechniciens] = useState<Technicien[]>([]);
  const [techniciensWithAccounts, setTechniciensWithAccounts] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'with_account' | 'without_account'>('all');
  // Detail modal
  const [selectedTechnicien, setSelectedTechnicien] = useState<TechnicienDetail | null>(null);
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Create account modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState<CreateAccountForm>({
    company_name: '',
    email: '',
    first_name: '',
    last_name: '',
    phone: ''
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Revoke account
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [revokeLoading, setRevokeLoading] = useState(false);

  // Update email modal
  const [showUpdateEmailModal, setShowUpdateEmailModal] = useState(false);
  const [updateEmailForm, setUpdateEmailForm] = useState({ email: '', phone: '' });
  const [updateEmailLoading, setUpdateEmailLoading] = useState(false);
  const [updateEmailError, setUpdateEmailError] = useState<string | null>(null);

  // Renvoyer le SMS (nouveau mot de passe temporaire)
  const [showResendModal, setShowResendModal] = useState(false);
  const [resendPhone, setResendPhone] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);

  // Viewport (responsive)
  const { isPhone } = useViewport();

  // Bandeau : vert 10 s quand c'est parti, ROUGE quand le SMS n'est pas parti
  // (un compte sans SMS n'a pas remis de code au technicien : ça se voit).
  const afficherMessage = (message: MessageCompte) => {
    if (message.ok) {
      setSuccessMessage(message.texte);
      setTimeout(() => setSuccessMessage(null), 10000);
    } else {
      setSuccessMessage(null);
      setError(message.texte);
      setTimeout(() => setError(null), 10000);
    }
  };

  // Charger les techniciens depuis l'API backend
  const fetchTechniciens = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('sso_token');
      if (!token) {
        throw new Error('Non authentifié');
      }

      // Charger les techniciens et les codes avec compte en parallèle
      const [techniciensResponse, accountsResponse] = await Promise.all([
        fetch(`${API_URL}/api/techniciens`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch(`${API_URL}/api/techniciens/with-accounts`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
      ]);

      if (!techniciensResponse.ok) {
        const errorData = await techniciensResponse.json();
        throw new Error(errorData.error?.message || 'Erreur serveur');
      }

      const techniciensData = await techniciensResponse.json();

      if (techniciensData.success) {
        setTechniciens(techniciensData.techniciens || []);
      } else {
        throw new Error(techniciensData.error?.message || 'Erreur lors du chargement');
      }

      // Charger les codes avec compte (non bloquant si erreur)
      if (accountsResponse.ok) {
        const accountsData = await accountsResponse.json();
        if (accountsData.success) {
          setTechniciensWithAccounts(accountsData.codes || []);
        }
      }
    } catch (err) {
      console.error('Erreur chargement techniciens:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canView) {
      fetchTechniciens();
    }
  }, [canView, fetchTechniciens]);

  // Refetch quand refreshTrigger change (navigation vers cet onglet)
  const isFirstMount = React.useRef(true);
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    if (canView) {
      console.log('[TechniciensPage] refreshTrigger changed, refetching...');
      fetchTechniciens();
    }
  }, [refreshTrigger, canView, fetchTechniciens]);

  // Charger le detail d'un technicien
  const fetchTechnicienDetail = async (code: string) => {
    setDetailLoading(true);
    setSelectedCode(code);

    try {
      const token = localStorage.getItem('sso_token');
      if (!token) {
        throw new Error('Non authentifié');
      }

      const response = await fetch(`${API_URL}/api/techniciens/${code}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Erreur serveur');
      }

      const data = await response.json();

      if (data.success) {
        setSelectedTechnicien(data.technicien);
        setShowDetailModal(true);

        // Pré-remplir le formulaire de création avec les infos du technicien
        const contact = data.technicien.contacts?.[0];
        const adresse = data.technicien.adresse;
        const ident = data.technicien.identification;

        setCreateForm({
          company_name: ident.nom || '',
          email: contact?.email || '',
          first_name: '',
          last_name: '',
          // L'IBM i rend le mobile en national (« 0696… ») : le champ veut du E.164,
          // et le bon indicatif (la Martinique est en +596, pas en +590).
          phone: versE164(contact?.mobile || adresse?.telephone1 || '') || ''
        });
      } else {
        throw new Error(data.error?.message || 'Erreur lors du chargement');
      }
    } catch (err) {
      console.error('Erreur chargement detail:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement');
    } finally {
      setDetailLoading(false);
    }
  };

  // Creer un compte mobile pour le technicien
  async function createTechnicienAccount() {
    const token = localStorage.getItem('sso_token');
    if (!token || !selectedTechnicien) return;

    // Validation des champs obligatoires
    if (!createForm.company_name.trim()) {
      setCreateError('Le nom de l\'entreprise est obligatoire.');
      return;
    }
    if (!createForm.phone) {
      setCreateError('Le numéro de téléphone portable est obligatoire.');
      return;
    }
    if (!isValidPhoneNumber(createForm.phone)) {
      setCreateError('Le numéro de téléphone portable est invalide.');
      return;
    }

    setCreateLoading(true);
    setCreateError(null);

    try {
      // Appeler l'endpoint backend qui gere l'auth Supabase
      const response = await fetch(
        `${API_URL}/api/techniciens/${selectedTechnicien.identification.code}/create-account`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            company_name: createForm.company_name,
            email: createForm.email,
            first_name: createForm.first_name || undefined,
            last_name: createForm.last_name || undefined,
            phone: createForm.phone || undefined
          })
        }
      );

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error?.message || result.error || 'Erreur lors de la création');
      }

      // Ce qui est réellement parti, dit tel quel (messageCreation, essayé au caractère près)
      const displayName = createForm.company_name || `${createForm.first_name} ${createForm.last_name}`.trim();
      afficherMessage(messageCreation(result.action, displayName, result.notifications || {}));

      // Ajouter le code à la liste des techniciens avec compte
      const newCode = selectedTechnicien.identification.code.toUpperCase();
      setTechniciensWithAccounts(prev =>
        prev.includes(newCode) ? prev : [...prev, newCode]
      );

      // Fermer les modals
      setShowCreateModal(false);
      setShowDetailModal(false);
      setSelectedTechnicien(null);

      // Reset form
      setCreateForm({
        company_name: '',
        email: '',
        first_name: '',
        last_name: '',
        phone: ''
      });

    } catch (err) {
      console.error('Erreur creation compte:', err);
      setCreateError(err instanceof Error ? err.message : 'Erreur lors de la création');
    } finally {
      setCreateLoading(false);
    }
  }

  // Révoquer le compte d'un technicien
  async function revokeAccount() {
    const token = localStorage.getItem('sso_token');
    if (!token || !selectedTechnicien) return;

    setRevokeLoading(true);

    try {
      const response = await fetch(
        `${API_URL}/api/techniciens/${selectedTechnicien.identification.code}/revoke-account`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error?.message || 'Erreur lors de la révocation');
      }

      const displayName = selectedTechnicien.identification.nom || selectedTechnicien.identification.code;
      setSuccessMessage(`Compte de ${displayName} révoqué et archivé.`);
      setTimeout(() => setSuccessMessage(null), 5000);

      // Retirer le code de la liste des comptes actifs
      const code = selectedTechnicien.identification.code.toUpperCase();
      setTechniciensWithAccounts(prev => prev.filter(c => c !== code));

      setShowRevokeConfirm(false);
      setShowDetailModal(false);
      setSelectedTechnicien(null);
    } catch (err) {
      console.error('Erreur révocation:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors de la révocation');
      setShowRevokeConfirm(false);
    } finally {
      setRevokeLoading(false);
    }
  }

  // Modifier l'email d'un technicien
  async function updateTechnicienEmail() {
    const token = localStorage.getItem('sso_token');
    if (!token || !selectedTechnicien) return;

    if (!updateEmailForm.email.trim()) {
      setUpdateEmailError('Le nouvel email est obligatoire.');
      return;
    }
    if (!updateEmailForm.phone) {
      setUpdateEmailError('Le numéro de téléphone portable est obligatoire.');
      return;
    }
    if (!isValidPhoneNumber(updateEmailForm.phone)) {
      setUpdateEmailError('Le numéro de téléphone portable est invalide.');
      return;
    }

    setUpdateEmailLoading(true);
    setUpdateEmailError(null);

    try {
      const response = await fetch(
        `${API_URL}/api/techniciens/${selectedTechnicien.identification.code}/update-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            email: updateEmailForm.email,
            phone: updateEmailForm.phone
          })
        }
      );

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error?.message || 'Erreur lors de la modification');
      }

      const displayName = selectedTechnicien.identification.nom || selectedTechnicien.identification.code;
      afficherMessage(messageModificationEmail(displayName, result.notifications || {}));

      setShowUpdateEmailModal(false);
      setShowDetailModal(false);
      setSelectedTechnicien(null);
      setUpdateEmailForm({ email: '', phone: '' });
    } catch (err) {
      console.error('Erreur modification email:', err);
      setUpdateEmailError(err instanceof Error ? err.message : 'Erreur lors de la modification');
    } finally {
      setUpdateEmailLoading(false);
    }
  }

  // Renvoyer le SMS : un nouveau mot de passe temporaire pour un compte existant
  async function renvoyerSms() {
    const token = localStorage.getItem('sso_token');
    if (!token || !selectedTechnicien) return;

    // Champ facultatif : vide = le numéro enregistré sur le compte ; rempli, il doit être valide
    if (resendPhone && !isValidPhoneNumber(resendPhone)) {
      setResendError('Le numéro de téléphone portable est invalide.');
      return;
    }

    setResendLoading(true);
    setResendError(null);

    try {
      const code = selectedTechnicien.identification.code;
      console.log('[TechniciensPage] Renvoi du SMS pour le technicien', code);

      const response = await fetch(
        `${API_URL}/api/techniciens/${code}/resend-sms`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          // Le numéro corrigé ne part que s'il est saisi (corps facultatif du contrat)
          body: JSON.stringify(resendPhone ? { phone: resendPhone } : {})
        }
      );

      const result = await response.json();

      if (!result.success) {
        // Les messages d'erreur du contrat sont en français : ils se lisent tels quels
        throw new Error(result.error?.message || "Erreur lors de l'envoi du SMS");
      }

      afficherMessage({ texte: messageRenvoiSms(result.notifications || {}), ok: true });

      setShowResendModal(false);
      setShowDetailModal(false);
      setSelectedTechnicien(null);
      setResendPhone('');
    } catch (err) {
      // La fenêtre reste ouverte : l'administrateur corrige le numéro et renvoie
      console.error('[TechniciensPage] Renvoi du SMS en échec:', err);
      setResendError(err instanceof Error ? err.message : "Erreur lors de l'envoi du SMS");
    } finally {
      setResendLoading(false);
    }
  }

  // Le pupitre du champ téléphone : l'indicatif du mobile de l'IBM i pour ce
  // technicien, sinon la Guadeloupe (comportement d'avant).
  const mobileIbmi = selectedTechnicien
    ? (selectedTechnicien.contacts?.[0]?.mobile || selectedTechnicien.adresse?.telephone1 || '')
    : '';
  const paysDefaut = paysDuNumero(versE164(mobileIbmi)) || 'GP';

  // Filtrer les techniciens
  const filteredTechniciens = techniciens.filter(tech => {
    const matchesSearch = searchTerm === '' ||
      tech.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tech.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tech.ville.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tech.codePostal.includes(searchTerm);

    const hasAccount = techniciensWithAccounts.includes(tech.code.toUpperCase());
    const matchesFilter = filterStatus === 'all' ||
      (filterStatus === 'with_account' && hasAccount) ||
      (filterStatus === 'without_account' && !hasAccount);

    return matchesSearch && matchesFilter;
  });

  // Gate d'entree : permission technicians.view
  if (!canView) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '60vh',
        gap: '16px'
      }}>
        <ShieldIcon size={64} color="#ef4444" />
        <h2 style={{ color: '#1e293b', margin: 0 }}>Accès refusé</h2>
        <p style={{ color: '#64748b', margin: 0 }}>
          Vous n'avez pas les autorisations nécessaires pour accéder à cette page.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Styles pour PhoneInput */}
      <style>{phoneInputStyles}</style>

      {/* Header */}
      <div style={{
        display: 'flex',
        flexDirection: isPhone ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isPhone ? 'stretch' : 'center',
        gap: isPhone ? 12 : 0,
        marginBottom: '24px'
      }}>
        <div>
          <h1 style={{
            fontSize: '24px',
            fontWeight: '600',
            color: '#1e293b',
            margin: '0 0 8px 0'
          }}>
            Techniciens
          </h1>
          <p style={{ color: '#64748b', margin: 0 }}>
            Sous-traitants disponibles pour création de compte mobile
          </p>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={fetchTechniciens}
            disabled={loading}
            title="Actualiser les données"
            style={{
              flex: isPhone ? 1 : undefined,
              display: 'flex',
              alignItems: 'center',
              justifyContent: isPhone ? 'center' : undefined,
              gap: '8px',
              padding: '10px 20px',
              backgroundColor: '#f1f5f9',
              color: '#475569',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.15s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e2e8f0'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
          >
            <RefreshIcon
              size={16}
              style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }}
            />
            Actualiser
          </button>
        </div>
      </div>

      {/* Messages */}
      {successMessage && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '12px 16px',
          backgroundColor: '#dcfce7',
          border: '1px solid #86efac',
          borderRadius: '8px',
          marginBottom: '16px',
          color: '#166534'
        }}>
          <CheckIcon size={20} color="#16a34a" />
          {successMessage}
        </div>
      )}

      {error && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '12px 16px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          marginBottom: '16px',
          color: '#dc2626'
        }}>
          <CloseIcon size={20} color="#dc2626" />
          {error}
          <button
            onClick={() => setError(null)}
            style={{
              marginLeft: 'auto',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px'
            }}
          >
            <CloseIcon size={16} color="#dc2626" />
          </button>
        </div>
      )}

      {/* Stats (cliquables = filtres) */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: isPhone ? '8px' : '16px',
        marginBottom: isPhone ? '16px' : '24px'
      }}>
        <StatCard
          icon={<WrenchIcon size={24} color="#3b82f6" />}
          label="Techniciens"
          value={techniciens.length}
          bgColor="#dbeafe"
          accentColor="#3b82f6"
          active={filterStatus === 'all'}
          onClick={() => setFilterStatus('all')}
        />
        <StatCard
          icon={<CheckIcon size={24} color="#22c55e" />}
          label="Comptes créés"
          value={techniciensWithAccounts.length}
          bgColor="#dcfce7"
          accentColor="#22c55e"
          active={filterStatus === 'with_account'}
          onClick={() => setFilterStatus(filterStatus === 'with_account' ? 'all' : 'with_account')}
        />
        <StatCard
          icon={<UserIcon size={24} color="#f59e0b" />}
          label="Sans compte"
          value={techniciens.length - techniciensWithAccounts.length}
          bgColor="#fef3c7"
          accentColor="#f59e0b"
          active={filterStatus === 'without_account'}
          onClick={() => setFilterStatus(filterStatus === 'without_account' ? 'all' : 'without_account')}
        />
      </div>

      {/* Recherche */}
      <div style={{
        display: 'flex',
        gap: '16px',
        marginBottom: '20px'
      }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <SearchIcon
            size={18}
            color="#94a3b8"
            style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)'
            }}
          />
          <input
            type="text"
            placeholder="Rechercher par nom, code, ville..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px 10px 40px',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none'
            }}
          />
        </div>
      </div>

      {/* Liste des techniciens */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        overflow: 'hidden'
      }}>
        {loading ? (
          <div style={{
            padding: '48px',
            textAlign: 'center',
            color: '#64748b'
          }}>
            <RefreshIcon
              size={32}
              color="#94a3b8"
              style={{ animation: 'spin 1s linear infinite', marginBottom: '12px' }}
            />
            <div>Chargement des techniciens...</div>
          </div>
        ) : filteredTechniciens.length === 0 ? (
          <div style={{
            padding: '48px',
            textAlign: 'center',
            color: '#64748b'
          }}>
            <WrenchIcon size={48} color="#cbd5e1" style={{ marginBottom: '12px' }} />
            <div>Aucun technicien trouvé</div>
          </div>
        ) : (
          <div>
            {filteredTechniciens.map((tech, index) => (
              <div
                key={tech.code}
                onClick={() => fetchTechnicienDetail(tech.code)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: isPhone ? '12px 14px' : '16px 20px',
                  borderBottom: index < filteredTechniciens.length - 1 ? '1px solid #e2e8f0' : 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.15s',
                  backgroundColor: detailLoading && selectedCode === tech.code ? '#f8fafc' : 'transparent'
                }}
                onMouseEnter={(e) => {
                  if (!(detailLoading && selectedCode === tech.code)) {
                    e.currentTarget.style.backgroundColor = '#f8fafc';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!(detailLoading && selectedCode === tech.code)) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {/* Avatar */}
                <div style={{
                  width: isPhone ? '40px' : '48px',
                  height: isPhone ? '40px' : '48px',
                  backgroundColor: '#dbeafe',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: isPhone ? '12px' : '16px',
                  flexShrink: 0
                }}>
                  <WrenchIcon size={24} color="#3b82f6" />
                </div>

                {/* Infos principales */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '4px'
                  }}>
                    <span style={{
                      fontWeight: '600',
                      color: '#1e293b',
                      fontSize: '15px'
                    }}>
                      {tech.nom}
                    </span>
                    <span style={{
                      padding: '2px 8px',
                      backgroundColor: '#f1f5f9',
                      borderRadius: '4px',
                      fontSize: '12px',
                      color: '#64748b',
                      fontFamily: 'monospace'
                    }}>
                      {tech.code}
                    </span>
                    {techniciensWithAccounts.includes(tech.code.toUpperCase()) && (
                      <span style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '2px 8px',
                        backgroundColor: '#dcfce7',
                        borderRadius: '4px',
                        fontSize: '11px',
                        color: '#166534',
                        fontWeight: 500
                      }}>
                        <CheckIcon size={12} color="#22c55e" />
                        Compte créé
                      </span>
                    )}
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: isPhone ? 'wrap' : 'nowrap',
                    gap: isPhone ? '8px' : '16px',
                    fontSize: '13px',
                    color: '#64748b'
                  }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <MapPinIcon size={14} color="#94a3b8" />
                      {tech.codePostal} {tech.ville}
                    </span>
                    {tech.telephone && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <PhoneIcon size={14} color="#94a3b8" />
                        {tech.telephone}
                      </span>
                    )}
                  </div>
                </div>

                {/* Fleche */}
                <div style={{ flexShrink: 0 }}>
                  {detailLoading && selectedCode === tech.code ? (
                    <RefreshIcon
                      size={20}
                      color="#94a3b8"
                      style={{ animation: 'spin 1s linear infinite' }}
                    />
                  ) : (
                    <ChevronRightIcon size={20} color="#94a3b8" />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Detail */}
      {showDetailModal && selectedTechnicien && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: isPhone ? '16px' : '24px',
            width: '100%',
            maxWidth: isPhone ? 'calc(100vw - 24px)' : '600px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            {/* Header modal */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px'
            }}>
              <h2 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#1e293b',
                margin: 0
              }}>
                Détail du technicien
              </h2>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedTechnicien(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <CloseIcon size={20} color="#64748b" />
              </button>
            </div>

            {/* Bloc 1 : Identification */}
            <div style={{
              backgroundColor: '#f8fafc',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '16px'
            }}>
              <h3 style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#475569',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <UserIcon size={16} color="#3b82f6" />
                Identification
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <InfoField label="Code" value={selectedTechnicien.identification.code} />
                <InfoField label="Type" value={selectedTechnicien.identification.typeFournisseur === 'ST' ? 'Sous-traitant' : selectedTechnicien.identification.typeFournisseur} />
                <div style={{ gridColumn: '1 / -1' }}>
                  <InfoField label="Nom" value={selectedTechnicien.identification.nom} />
                </div>
              </div>
            </div>

            {/* Bloc 2 : Adresse */}
            <div style={{
              backgroundColor: '#f8fafc',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '16px'
            }}>
              <h3 style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#475569',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <MapPinIcon size={16} color="#10b981" />
                Adresse
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <InfoField label="Adresse" value={[
                    selectedTechnicien.adresse.adresse1,
                    selectedTechnicien.adresse.adresse2,
                    selectedTechnicien.adresse.adresse3
                  ].filter(Boolean).join(', ') || '-'} />
                </div>
                <InfoField label="Code postal" value={selectedTechnicien.adresse.codePostal} />
                <InfoField label="Ville" value={selectedTechnicien.adresse.ville} />
                <InfoField label="Téléphone 1" value={selectedTechnicien.adresse.telephone1} />
                <InfoField label="Téléphone 2" value={selectedTechnicien.adresse.telephone2} />
                <InfoField label="Fax" value={selectedTechnicien.adresse.fax} />
                <InfoField label="Contact" value={selectedTechnicien.adresse.responsableContact} />
              </div>
            </div>

            {/* Bloc 3 : Contacts */}
            <div style={{
              backgroundColor: '#f8fafc',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <h3 style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#475569',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <PhoneIcon size={16} color="#f59e0b" />
                Correspondants ({selectedTechnicien.contacts.length})
              </h3>
              {selectedTechnicien.contacts.length === 0 ? (
                <p style={{ color: '#94a3b8', fontSize: '14px', margin: 0 }}>
                  Aucun correspondant enregistré
                </p>
              ) : (
                selectedTechnicien.contacts.map((contact, idx) => (
                  <div
                    key={idx}
                    style={{
                      backgroundColor: 'white',
                      borderRadius: '6px',
                      padding: '12px',
                      marginBottom: idx < selectedTechnicien.contacts.length - 1 ? '8px' : 0,
                      border: '1px solid #e2e8f0'
                    }}
                  >
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                      <InfoField label="Nom" value={contact.nomCorrespondant} small />
                      <InfoField label="Ligne directe" value={contact.ligneDirecte} small />
                      <InfoField label="Mobile" value={contact.mobile} small />
                      <InfoField label="Email" value={contact.email} small />
                      {contact.observations && (
                        <div style={{ gridColumn: '1 / -1' }}>
                          <InfoField label="Observations" value={contact.observations} small />
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Actions selon état du compte (conditionnees par les permissions) */}
            {techniciensWithAccounts.includes(selectedTechnicien.identification.code.toUpperCase()) ? (
              // flexWrap : trois boutons ne doivent jamais déborder de la fenêtre
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                {canUpdateEmail && (
                  <button
                    onClick={() => {
                      setUpdateEmailForm({ email: '', phone: '' });
                      setUpdateEmailError(null);
                      setShowUpdateEmailModal(true);
                    }}
                    style={{
                      flex: '1 1 150px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      padding: '14px 20px',
                      backgroundColor: '#f59e0b',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '15px',
                      fontWeight: '500'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#d97706'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#f59e0b'; }}
                  >
                    <MailIcon size={18} />
                    Modifier email
                  </button>
                )}
                {canCreateAccount && (
                  <button
                    onClick={() => {
                      setResendPhone('');
                      setResendError(null);
                      setShowResendModal(true);
                    }}
                    style={{
                      flex: '1 1 150px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      padding: '14px 20px',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '15px',
                      fontWeight: '500'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#2563eb'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#3b82f6'; }}
                  >
                    <KeyIcon size={18} />
                    Renvoyer le SMS
                  </button>
                )}
                {canRevokeAccount && (
                  <button
                    onClick={() => setShowRevokeConfirm(true)}
                    style={{
                      flex: '1 1 150px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      padding: '14px 20px',
                      backgroundColor: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '15px',
                      fontWeight: '500'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#dc2626'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#ef4444'; }}
                  >
                    <CloseIcon size={18} />
                    Révoquer
                  </button>
                )}
              </div>
            ) : (
              canCreateAccount && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '14px 20px',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '15px',
                    fontWeight: '500'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#2563eb'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#3b82f6'; }}
                >
                  <UserPlusIcon size={18} />
                  Créer un compte mobile
                </button>
              )
            )}
          </div>
        </div>
      )}

      {/* Modal Confirmation révocation */}
      {showRevokeConfirm && selectedTechnicien && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1002
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: isPhone ? '16px' : '24px',
            width: '100%',
            maxWidth: isPhone ? 'calc(100vw - 24px)' : '420px'
          }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b', margin: '0 0 16px 0' }}>
              Révoquer le compte
            </h2>
            <p style={{ color: '#475569', fontSize: '14px', margin: '0 0 8px 0' }}>
              Êtes-vous sûr de vouloir révoquer le compte de <strong>{selectedTechnicien.identification.nom || selectedTechnicien.identification.code}</strong> ?
            </p>
            <div style={{
              padding: '12px',
              backgroundColor: '#fef3c7',
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '13px',
              color: '#92400e'
            }}>
              Le compte sera archivé (désactivé). Le technicien ne pourra plus se connecter. Les données seront conservées.
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setShowRevokeConfirm(false)}
                disabled={revokeLoading}
                style={{
                  flex: 1, padding: '12px',
                  backgroundColor: '#f1f5f9', color: '#475569',
                  border: '1px solid #e2e8f0', borderRadius: '8px',
                  fontSize: '14px', fontWeight: '500', cursor: 'pointer'
                }}
              >
                Annuler
              </button>
              <button
                onClick={revokeAccount}
                disabled={revokeLoading}
                style={{
                  flex: 1, padding: '12px',
                  backgroundColor: revokeLoading ? '#fca5a5' : '#ef4444', color: 'white',
                  border: 'none', borderRadius: '8px',
                  fontSize: '14px', fontWeight: '500',
                  cursor: revokeLoading ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                }}
              >
                {revokeLoading ? 'Révocation...' : 'Révoquer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Modification email */}
      {showUpdateEmailModal && selectedTechnicien && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1002
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: isPhone ? '16px' : '24px',
            width: '100%',
            maxWidth: isPhone ? 'calc(100vw - 24px)' : '450px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px'
            }}>
              <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1e293b', margin: 0 }}>
                Modifier l'email
              </h2>
              <button
                onClick={() => { setShowUpdateEmailModal(false); setUpdateEmailError(null); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
              >
                <CloseIcon size={20} color="#64748b" />
              </button>
            </div>

            <div style={{
              padding: '12px',
              backgroundColor: '#fef3c7',
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: '13px',
              color: '#92400e'
            }}>
              L'ancien compte sera révoqué. Un nouveau compte sera créé avec le nouvel email.
              Le technicien recevra un nouvel email de bienvenue et un nouveau code SMS.
            </div>

            {updateEmailError && (
              <div style={{
                padding: '12px 16px',
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                marginBottom: '16px',
                color: '#dc2626',
                fontSize: '14px'
              }}>
                {updateEmailError}
              </div>
            )}

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                Sous-traitant
              </label>
              <div style={{
                padding: '12px 14px',
                backgroundColor: '#f3f4f6',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#374151',
                fontWeight: '500'
              }}>
                {selectedTechnicien.identification.nom || '-'}
              </div>
            </div>

            <FormField
              label="Nouvel email *"
              type="email"
              value={updateEmailForm.email}
              onChange={(v) => setUpdateEmailForm(prev => ({ ...prev, email: v }))}
              required
            />

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                Téléphone portable *
              </label>
              <PhoneInput
                international
                defaultCountry={paysDefaut}
                value={updateEmailForm.phone}
                onChange={(value) => setUpdateEmailForm(prev => ({ ...prev, phone: value || '' }))}
                placeholder="0690 12 34 56"
              />
              {updateEmailForm.phone && !isValidPhoneNumber(updateEmailForm.phone) && (
                <p style={{ fontSize: '12px', color: '#dc2626', marginTop: '4px', marginBottom: 0 }}>
                  Numéro de téléphone invalide
                </p>
              )}
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
              <button
                type="button"
                onClick={() => { setShowUpdateEmailModal(false); setUpdateEmailError(null); }}
                style={{
                  flex: 1, padding: '12px',
                  backgroundColor: '#f1f5f9', color: '#475569',
                  border: '1px solid #e2e8f0', borderRadius: '8px',
                  fontSize: '14px', fontWeight: '500', cursor: 'pointer'
                }}
              >
                Annuler
              </button>
              <button
                onClick={updateTechnicienEmail}
                disabled={updateEmailLoading}
                style={{
                  flex: 1, padding: '12px',
                  backgroundColor: updateEmailLoading ? '#fcd34d' : '#f59e0b', color: 'white',
                  border: 'none', borderRadius: '8px',
                  fontSize: '14px', fontWeight: '500',
                  cursor: updateEmailLoading ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                }}
              >
                {updateEmailLoading ? (
                  <>
                    <div style={{
                      width: '16px', height: '16px',
                      border: '2px solid white', borderTop: '2px solid transparent',
                      borderRadius: '50%', animation: 'spin 1s linear infinite'
                    }}></div>
                    Modification...
                  </>
                ) : (
                  <>
                    <MailIcon size={16} />
                    Modifier
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Renvoyer le SMS */}
      {showResendModal && selectedTechnicien && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1003
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: isPhone ? '16px' : '24px',
            width: '100%',
            maxWidth: isPhone ? 'calc(100vw - 24px)' : '450px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px'
            }}>
              <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1e293b', margin: 0 }}>
                Renvoyer le mot de passe par SMS
              </h2>
              <button
                onClick={() => { setShowResendModal(false); setResendError(null); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
              >
                <CloseIcon size={20} color="#64748b" />
              </button>
            </div>

            <div style={{
              padding: '12px',
              backgroundColor: '#fef3c7',
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: '13px',
              color: '#92400e'
            }}>
              Un nouveau mot de passe temporaire va être envoyé par SMS à{' '}
              {selectedTechnicien.identification.nom || selectedTechnicien.identification.code}.
              L'ancien ne fonctionnera plus. Le technicien recevra aussi, par courriel, le lien pour
              installer l'application.
            </div>

            {resendError && (
              <div style={{
                padding: '12px 16px',
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                marginBottom: '16px',
                color: '#dc2626',
                fontSize: '14px'
              }}>
                {resendError}
              </div>
            )}

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                Téléphone portable (facultatif)
              </label>
              <PhoneInput
                international
                defaultCountry={paysDefaut}
                value={resendPhone}
                onChange={(value) => setResendPhone(value || '')}
                placeholder="0690 12 34 56"
              />
              {resendPhone && !isValidPhoneNumber(resendPhone) && (
                <p style={{ fontSize: '12px', color: '#dc2626', marginTop: '4px', marginBottom: 0 }}>
                  Numéro de téléphone invalide
                </p>
              )}
              <p style={{
                fontSize: '12px',
                color: '#6b7280',
                marginTop: '6px',
                marginBottom: 0
              }}>
                Laissez vide pour utiliser le numéro enregistré sur le compte. Pour le corriger,
                saisissez-le au format international.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
              <button
                type="button"
                onClick={() => { setShowResendModal(false); setResendError(null); }}
                style={{
                  flex: 1, padding: '12px',
                  backgroundColor: '#f1f5f9', color: '#475569',
                  border: '1px solid #e2e8f0', borderRadius: '8px',
                  fontSize: '14px', fontWeight: '500', cursor: 'pointer'
                }}
              >
                Annuler
              </button>
              <button
                onClick={renvoyerSms}
                disabled={resendLoading}
                style={{
                  flex: 1, padding: '12px',
                  backgroundColor: resendLoading ? '#93c5fd' : '#3b82f6', color: 'white',
                  border: 'none', borderRadius: '8px',
                  fontSize: '14px', fontWeight: '500',
                  cursor: resendLoading ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                }}
              >
                {resendLoading ? (
                  <>
                    <div style={{
                      width: '16px', height: '16px',
                      border: '2px solid white', borderTop: '2px solid transparent',
                      borderRadius: '50%', animation: 'spin 1s linear infinite'
                    }}></div>
                    Envoi en cours…
                  </>
                ) : (
                  <>
                    <KeyIcon size={16} />
                    Envoyer le SMS
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Creation compte */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: isPhone ? '16px' : '24px',
            width: '100%',
            maxWidth: isPhone ? 'calc(100vw - 24px)' : '450px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px'
            }}>
              <h2 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#1e293b',
                margin: 0
              }}>
                Créer un compte mobile
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setCreateError(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <CloseIcon size={20} color="#64748b" />
              </button>
            </div>

            {createError && (
              <div style={{
                padding: '12px 16px',
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                marginBottom: '16px',
                color: '#dc2626',
                fontSize: '14px'
              }}>
                {createError}
              </div>
            )}

            <form onSubmit={(e) => {
              e.preventDefault();
              createTechnicienAccount();
            }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px'
                }}>
                  Sous-traitant
                </label>
                <div style={{
                  padding: '12px 14px',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: '#374151',
                  fontWeight: '500'
                }}>
                  {createForm.company_name || '-'}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <FormField
                  label="Prénom"
                  value={createForm.first_name}
                  onChange={(v) => setCreateForm(prev => ({ ...prev, first_name: v }))}
                />
                <FormField
                  label="Nom"
                  value={createForm.last_name}
                  onChange={(v) => setCreateForm(prev => ({ ...prev, last_name: v }))}
                />
              </div>

              <FormField
                label="Email *"
                type="email"
                value={createForm.email}
                onChange={(v) => setCreateForm(prev => ({ ...prev, email: v }))}
                required
              />

              {/* Téléphone portable avec sélecteur de pays */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px'
                }}>
                  Téléphone portable *
                </label>
                <PhoneInput
                  international
                  defaultCountry={paysDefaut}
                  value={createForm.phone}
                  onChange={(value) => setCreateForm(prev => ({ ...prev, phone: value || '' }))}
                  placeholder="0690 12 34 56"
                />
                {createForm.phone && !isValidPhoneNumber(createForm.phone) && (
                  <p style={{
                    fontSize: '12px',
                    color: '#dc2626',
                    marginTop: '4px',
                    marginBottom: 0
                  }}>
                    Numéro de téléphone invalide
                  </p>
                )}
                <p style={{
                  fontSize: '12px',
                  color: '#6b7280',
                  marginTop: '6px',
                  marginBottom: 0
                }}>
                  Ce numéro sera utilisé pour envoyer le code d'accès à l'application mobile.
                </p>
              </div>

              <div style={{
                padding: '12px',
                backgroundColor: '#f0f9ff',
                borderRadius: '8px',
                marginBottom: '16px',
                fontSize: '13px',
                color: '#0369a1'
              }}>
                Un email sera envoyé au technicien avec ses identifiants de connexion.<br />
                Code technicien : <strong>{selectedTechnicien?.identification.code}</strong>
              </div>

              <div style={{
                display: 'flex',
                gap: '12px',
                marginTop: '8px'
              }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setCreateError(null);
                  }}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: '#f1f5f9',
                    color: '#475569',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={createLoading}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: createLoading ? '#93c5fd' : '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: createLoading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  {createLoading ? (
                    <>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid white',
                        borderTop: '2px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      Création...
                    </>
                  ) : (
                    <>
                      <UserPlusIcon size={16} />
                      Créer
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// Composants auxiliaires
interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  bgColor: string;
  accentColor?: string;
  active?: boolean;
  onClick?: () => void;
}

function StatCard({ icon, label, value, bgColor, accentColor, active, onClick }: StatCardProps) {
  const { isPhone } = useViewport();
  return (
    <div
      onClick={onClick}
      style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: isPhone ? '10px' : '20px',
        border: active ? `2px solid ${accentColor || '#3b82f6'}` : '1px solid #e2e8f0',
        display: 'flex',
        flexDirection: isPhone ? 'column' : 'row',
        alignItems: isPhone ? 'flex-start' : 'center',
        gap: isPhone ? '6px' : '16px',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.15s ease',
        boxShadow: active ? `0 0 0 1px ${accentColor || '#3b82f6'}20` : 'none',
        minWidth: 0
      }}
    >
      <div style={{
        width: isPhone ? '32px' : '48px',
        height: isPhone ? '32px' : '48px',
        backgroundColor: bgColor,
        borderRadius: isPhone ? '8px' : '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      }}>
        {React.cloneElement(icon as React.ReactElement<{ size?: number }>, { size: isPhone ? 18 : 24 })}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{
          fontSize: isPhone ? '20px' : '28px',
          fontWeight: '600',
          color: '#1e293b',
          lineHeight: 1.1
        }}>
          {value}
        </div>
        <div style={{
          fontSize: isPhone ? '12px' : '14px',
          color: active ? (accentColor || '#3b82f6') : '#64748b',
          fontWeight: active ? '600' : '400',
          lineHeight: 1.2
        }}>
          {label}
        </div>
      </div>
    </div>
  );
}

interface InfoFieldProps {
  label: string;
  value: string;
  small?: boolean;
}

function InfoField({ label, value, small = false }: InfoFieldProps) {
  return (
    <div>
      <div style={{
        fontSize: small ? '11px' : '12px',
        color: '#94a3b8',
        marginBottom: '2px'
      }}>
        {label}
      </div>
      <div style={{
        fontSize: small ? '13px' : '14px',
        color: value && value !== '-' ? '#1e293b' : '#cbd5e1',
        fontWeight: '500'
      }}>
        {value || '-'}
      </div>
    </div>
  );
}

interface FormFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  required?: boolean;
  placeholder?: string;
}

function FormField({ label, value, onChange, type = 'text', required = false, placeholder }: FormFieldProps) {
  return (
    <div style={{ marginBottom: '16px' }}>
      <label style={{
        display: 'block',
        fontSize: '14px',
        fontWeight: '500',
        color: '#374151',
        marginBottom: '6px'
      }}>
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        style={{
          width: '100%',
          padding: '12px 14px',
          border: '1px solid #d1d5db',
          borderRadius: '8px',
          fontSize: '14px',
          outline: 'none',
          boxSizing: 'border-box'
        }}
      />
    </div>
  );
}

export default TechniciensPage;
